/**
 * The door to the `notice-lines` chunk (noticeLines.ts, 2026-10-08): the notice
 * language rule, the viewer's notice language, the remembered language and the
 * "Prest0n isn't in this thread" line. The engine (and the tools on the first
 * download) keep only this door.
 *
 * The readers here answer synchronously: by the chunk's rule once it has
 * arrived, and by the device's language until then — the chunk is fetched at
 * the first idle moment once the chat is up (`useNoticeLines`), long before
 * anyone sends, so the fallback is a first-second fact, never another
 * person's words. `loadNoticeLines()` fetches it at once for a line that needs
 * it now. Nothing here throws.
 */
import { createOnDemandModule } from '../../utils/onDemandModule';
import { getUserUiLanguage } from '../threadMetadata/uiLanguage';
// TYPE-only imports: erased at build, never an edge into the chunk.
import type * as NoticeLinesNamespace from './noticeLines';
import type { NoticeLanguageRow } from './viewerNoticeLanguage';

export type NoticeLinesModule = typeof NoticeLinesNamespace;
export type { NoticeLanguageRow };

const lines = createOnDemandModule<NoticeLinesModule>(
  () => import(/* webpackChunkName: "notice-lines" */ './noticeLines'),
);

let ready: NoticeLinesModule | null = null;

export function noticeLinesNow(): NoticeLinesModule | null {
  return ready;
}

export function loadNoticeLines(): Promise<NoticeLinesModule> {
  return lines.load().then((mod) => {
    ready = mod;
    return mod;
  });
}

/** Fetch the chunk at the first idle moment once the chat is up; re-render the caller when it lands. */
export function useNoticeLines(): NoticeLinesModule | null {
  const mod = lines.useModule(true);
  if (mod) ready = mod;
  return mod;
}

function deviceLanguage(): 'en' | 'es' {
  try {
    return getUserUiLanguage() === 'es' ? 'es' : 'en';
  } catch {
    return 'en';
  }
}

function warm(): void {
  void loadNoticeLines().catch(() => { /* the device's language stands */ });
}

/** viewerNoticeLanguage.ts through the door. */
export function viewerNoticeLanguage(args: {
  viewerUid: string | null | undefined;
  ownLatestText?: string | null;
  messages?: ReadonlyArray<NoticeLanguageRow | null | undefined> | null;
}): 'en' | 'es' {
  if (ready) return ready.viewerNoticeLanguage(args);
  warm();
  return deviceLanguage();
}

/** The language of one ask the viewer made (a card's words), through the door. */
export function askNoticeLanguage(text: string | null | undefined): 'en' | 'es' {
  if (ready) return ready.askNoticeLanguage(text);
  warm();
  return deviceLanguage();
}

/** writingLanguage.noteOwnMessageLanguage through the door; never awaited by a send. */
export function noteOwnMessageLanguage(uid: string | null | undefined, text: unknown): Promise<void> {
  return loadNoticeLines()
    .then((mod) => mod.noteOwnMessageLanguage(uid, text))
    .catch(() => { /* the chunk did not arrive: nothing is remembered this time */ });
}

/** Tests only. */
export function resetNoticeLinesForTests(): void {
  ready = null;
  lines.resetForTests();
}
